import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trash2, ChevronRight, Image as ImageIcon,
  X, AlertTriangle, Loader2, FileText,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatMoney, getMerchantAvatar } from "../utils/mindeeParser";
import { formatGBP, formatReceiptDate, getCategoryPill } from "../utils/formatters";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GRADIENT_HEADER = "linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)";
const PRIMARY = "#2D5BFF";

// ─── Named exports (still used by ReceiptDetailPage.js) ───────────────────────

export const Shimmer = ({ width = "100%", height = 12, radius = 6, style = {} }) => (
  <div
    className="animate-pulse"
    style={{ width, height, borderRadius: radius, backgroundColor: "#E5E7EB", ...style }}
  />
);

export const Lightbox = ({ url, onClose }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [isDrag, setIsDrag] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  const onWheel = (e) => {
    e.preventDefault();
    setScale((s) => Math.min(Math.max(s + (e.deltaY > 0 ? -0.1 : 0.1), 0.5), 4));
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
      onWheel={onWheel}
      onMouseUp={() => setIsDrag(false)}
      onMouseLeave={() => setIsDrag(false)}
      onMouseMove={(e) => {
        if (!isDrag) return;
        setPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
      }}
      data-testid="receipt-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5 text-white" />
      </button>
      <img
        src={url}
        alt="Receipt"
        className="max-w-full max-h-full select-none cursor-grab active:cursor-grabbing"
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transition: isDrag ? "none" : "transform 0.1s ease-out",
          imageOrientation: "from-image",
        }}
        draggable={false}
        onMouseDown={(e) => {
          setIsDrag(true);
          dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
        }}
      />
    </div>
  );
};

export const ImagePlaceholder = ({ height = 180 }) => (
  <div
    className="flex flex-col items-center justify-center"
    style={{ height, backgroundColor: "#F3F4F6", borderRadius: 12, color: "#9CA3AF", gap: 6 }}
  >
    <ImageIcon className="w-8 h-8" />
    <span style={{ fontSize: 11, fontFamily: "DM Sans, sans-serif" }}>Image unavailable</span>
  </div>
);

export const DeleteDialog = ({ onCancel, onConfirm, isDeleting }) => (
  <div
    className="fixed inset-0 flex items-center justify-center p-6 z-50"
    style={{ backgroundColor: "rgba(15,23,42,0.5)" }}
    data-testid="delete-dialog"
  >
    <div className="text-center max-w-xs w-full" style={{ backgroundColor: "#FFFFFF", borderRadius: 20, padding: "28px 24px", boxShadow: "0 24px 48px rgba(0,0,0,0.2)" }}>
      <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
        <AlertTriangle className="w-7 h-7" style={{ color: "#EF4444" }} />
      </div>
      <h3 className="font-bold mb-1" style={{ color: "#111827", fontSize: 16, fontFamily: "Manrope, sans-serif" }}>
        Delete receipt?
      </h3>
      <p className="mb-5" style={{ color: "#6B7280", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>
        This will permanently delete this receipt. This cannot be undone.
      </p>
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          disabled={isDeleting}
          className="flex-1 rounded-full font-semibold"
          style={{ backgroundColor: "#F3F4F6", color: "#374151", padding: "10px 0", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}
          data-testid="delete-cancel"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isDeleting}
          className="flex-1 rounded-full font-semibold flex items-center justify-center"
          style={{ backgroundColor: "#EF4444", color: "#FFFFFF", padding: "10px 0", fontSize: 13, fontFamily: "DM Sans, sans-serif" }}
          data-testid="delete-confirm"
        >
          {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

// ─── Internal helpers ─────────────────────────────────────────────────────────

const MerchantAvatar = ({ name, size = 44 }) => {
  const { initials, bgColor, textColor } = getMerchantAvatar(name || "");
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: size * 0.25,
        backgroundColor: bgColor, color: textColor,
        fontWeight: 700, fontSize: size * 0.32, fontFamily: "Manrope, sans-serif",
        border: "1.5px solid rgba(0,0,0,0.08)",
      }}
    >
      {initials}
    </div>
  );
};

const SkeletonSummary = () => (
  <div className="animate-pulse" style={{ padding: "0 18px" }}>
    <div style={{ width: "90%", height: 13, borderRadius: 5, backgroundColor: "#E5E7EB", marginBottom: 10 }} />
    <div style={{ width: "80%", height: 13, borderRadius: 5, backgroundColor: "#E5E7EB", marginBottom: 10 }} />
    <div style={{ width: "55%", height: 13, borderRadius: 5, backgroundColor: "#F3F4F6" }} />
  </div>
);

// ─── Default export: ReceiptSummarySheet ──────────────────────────────────────
// Mobile  → slides up from the bottom as a bottom sheet
// Desktop → centred modal with backdrop
// Props:
//   isOpen      boolean
//   receiptId   string | null
//   onClose     () => void
//   isMobile    boolean
//   onDelete    (id: string) => void   — called after successful delete
// ─────────────────────────────────────────────────────────────────────────────
export default function ReceiptSummarySheet({ isOpen, receiptId, onClose, isMobile, onDelete }) {
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const reqRef = useRef(0);

  // Lock body scroll while open
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape" && isOpen && !showDeleteConfirm) onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isOpen, onClose, showDeleteConfirm]);

  // Fetch receipt
  const fetchReceipt = useCallback(async () => {
    if (!receiptId) return;
    const id = ++reqRef.current;
    setLoading(true);
    setReceipt(null);
    try {
      const res = await axios.get(`${API}/receipts/${receiptId}`, { withCredentials: true });
      if (id === reqRef.current) setReceipt(res.data.receipt ?? res.data);
    } catch {
      if (id === reqRef.current) toast.error("Failed to load receipt");
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
  }, [receiptId]);

  useEffect(() => {
    if (isOpen && receiptId) fetchReceipt();
  }, [isOpen, receiptId, fetchReceipt]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/receipts/${receiptId}`, { withCredentials: true });
      toast.success("Receipt deleted");
      onDelete?.(receiptId);
      onClose();
    } catch {
      toast.error("Failed to delete receipt");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleViewFull = () => {
    onClose();
    navigate(`/receipts/${receiptId}`);
  };

  if (!isOpen) return null;

  // Read directly from Claude-structured receipt document
  const currency    = receipt?.currency || "GBP";
  const items       = Array.isArray(receipt?.items) ? receipt.items : [];
  const previewItems = items.slice(0, 3);
  const totalItems   = items.length;
  const merchant    = receipt?.merchant || "—";
  const dateStr     = receipt ? formatReceiptDate(receipt.transaction_date || receipt.created_at) : "";
  const total       = receipt ? formatGBP(receipt.amount) : "—";
  const category    = receipt?.category || null;
  const imageUrl    = receipt?.receipt_image_url || null;
  const pill        = getCategoryPill(category);

  // ── Sheet content (shared between mobile + desktop) ───────────────────────
  const content = (
    <>
      {/* Drag handle — mobile only */}
      {isMobile && (
        <div
          role="button"
          aria-label="Close"
          onClick={onClose}
          style={{ cursor: "grab", padding: "10px 0 6px", display: "flex", justifyContent: "center" }}
          data-testid="drag-handle"
        >
          <div style={{ width: 36, height: 4, backgroundColor: "#E5E7EB", borderRadius: 2 }} />
        </div>
      )}

      {/* Merchant header */}
      <div
        className="flex items-center justify-between"
        style={{ padding: isMobile ? "14px 18px 0" : "20px 24px 0" }}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <MerchantAvatar name={merchant} size={44} />
          <div className="min-w-0">
            <p
              style={{
                fontFamily: "Manrope, sans-serif", fontWeight: 700, fontSize: 16,
                color: "#111827", margin: 0,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                maxWidth: isMobile ? 150 : 240,
              }}
              data-testid="summary-merchant-name"
            >
              {merchant}
            </p>
            <p style={{ fontFamily: "DM Sans, sans-serif", fontSize: 13, color: "#9CA3AF", margin: 0 }}>
              {dateStr}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <p
            style={{ fontFamily: "Manrope, sans-serif", fontWeight: 700, fontSize: 20, color: "#111827" }}
            data-testid="summary-total"
          >
            {total}
          </p>
          {/* Desktop close button */}
          {!isMobile && (
            <button
              onClick={onClose}
              style={{
                width: 32, height: 32, borderRadius: 8,
                border: "1px solid #F3F4F6", backgroundColor: "#F9FAFB",
                display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
              }}
              aria-label="Close"
              data-testid="summary-close-btn"
            >
              <X className="w-4 h-4" style={{ color: "#6B7280" }} />
            </button>
          )}
        </div>
      </div>

      {/* Category + item count */}
      <div
        className="flex items-center gap-2"
        style={{ padding: isMobile ? "10px 18px 0" : "10px 24px 0" }}
      >
        {category && (
          <span
            className="rounded-full"
            style={{
              backgroundColor: pill.bg, color: pill.color,
              fontSize: 12, fontWeight: 600, padding: "3px 10px",
              fontFamily: "DM Sans, sans-serif",
            }}
          >
            {category}
          </span>
        )}
        {totalItems > 0 && (
          <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "DM Sans, sans-serif" }}>
            {totalItems} item{totalItems !== 1 ? "s" : ""} captured
          </span>
        )}
      </div>

      {/* Divider */}
      <div style={{ height: 1, backgroundColor: "#F3F4F6", margin: "14px 0" }} />

      {/* Items preview — no thumbnail */}
      {loading ? (
        <SkeletonSummary />
      ) : (
        <div style={{ padding: isMobile ? "0 18px" : "0 24px" }}>
          {previewItems.length > 0 ? (
            <>
              <p
                style={{
                  fontSize: 11, color: "#9CA3AF", fontWeight: 600,
                  fontFamily: "DM Sans, sans-serif", margin: "0 0 8px",
                  letterSpacing: "0.05em", textTransform: "uppercase",
                }}
              >
                Items
              </p>
              {previewItems.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-baseline justify-between"
                  style={{ marginBottom: 7 }}
                >
                  <span
                    style={{
                      fontSize: 13, color: "#374151", fontFamily: "DM Sans, sans-serif",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      maxWidth: "65%",
                    }}
                  >
                    {item.name}
                  </span>
                  <span
                    style={{
                      fontSize: 13, fontWeight: 600, color: "#111827",
                      fontFamily: "Manrope, sans-serif", flexShrink: 0,
                    }}
                  >
                    {formatMoney(item.final_total ?? item.unit_price ?? item.total_amount ?? item.price, currency)}
                  </span>
                </div>
              ))}
            </>
          ) : (
            <p style={{ fontSize: 13, color: "#9CA3AF", fontFamily: "DM Sans, sans-serif", fontStyle: "italic" }}>
              No items captured
            </p>
          )}
        </div>
      )}

      {/* See all items / View full receipt */}
      {!loading && (
        <button
          onClick={handleViewFull}
          className="flex items-center justify-between"
          style={{
            margin: isMobile ? "12px 18px 0" : "14px 24px 0",
            width: isMobile ? "calc(100% - 36px)" : "calc(100% - 48px)",
            border: "1.5px solid #E5E7EB", borderRadius: 12,
            padding: "10px 14px", backgroundColor: "#FAFAFA", cursor: "pointer",
          }}
          data-testid="see-all-items-btn"
        >
          <span style={{ fontSize: 14, color: PRIMARY, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
            {totalItems > 3 ? `See all ${totalItems} items` : "View full receipt"}
          </span>
          <ChevronRight className="w-4 h-4" style={{ color: PRIMARY }} />
        </button>
      )}

      {/* Action bar */}
      {!loading && (
        <div
          className="flex items-center gap-3"
          style={{ padding: isMobile ? "14px 18px 4px" : "16px 24px 4px" }}
        >
          {/* View full receipt — primary CTA */}
          <button
            onClick={handleViewFull}
            className="flex-1 flex items-center justify-center gap-2"
            style={{
              height: 46, borderRadius: 12,
              background: GRADIENT_HEADER, color: "#FFFFFF",
              fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif",
              border: "none", cursor: "pointer",
            }}
            data-testid="view-full-receipt-btn"
          >
            <FileText className="w-4 h-4" />
            View full receipt
          </button>

          {/* Delete */}
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                width: 46, height: 46, borderRadius: 12,
                border: "1.5px solid #FEE2E2", backgroundColor: "#FFF5F5",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", flexShrink: 0,
              }}
              aria-label="Delete receipt"
              data-testid="delete-receipt-btn"
            >
              <Trash2 className="w-4 h-4" style={{ color: "#EF4444" }} />
            </button>
          ) : (
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                height: 46, borderRadius: 12, backgroundColor: "#EF4444",
                color: "#FFFFFF", fontSize: 13, fontWeight: 600,
                fontFamily: "DM Sans, sans-serif", padding: "0 16px",
                border: "none", cursor: deleting ? "not-allowed" : "pointer",
                opacity: deleting ? 0.7 : 1, flexShrink: 0,
                display: "flex", alignItems: "center",
              }}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {deleting ? "Deleting…" : "Confirm delete"}
            </button>
          )}
        </div>
      )}

      {/* Cancel confirm-delete */}
      {showDeleteConfirm && !deleting && (
        <button
          onClick={() => setShowDeleteConfirm(false)}
          style={{
            display: "block", margin: "6px auto 0",
            fontSize: 13, color: "#6B7280", fontFamily: "DM Sans, sans-serif",
            background: "none", border: "none", cursor: "pointer",
          }}
        >
          Cancel
        </button>
      )}
    </>
  );

  // Lightbox rendered at top level so it's never clipped by sheet overflow/stacking context
  const lightbox = showLightbox && imageUrl
    ? <Lightbox url={imageUrl} onClose={() => setShowLightbox(false)} />
    : null;

  // ── Mobile: bottom sheet ───────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <div
          onClick={onClose}
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 50 }}
          data-testid="sheet-overlay"
        />
        <div
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            backgroundColor: "#FFFFFF",
            borderRadius: "22px 22px 0 0",
            zIndex: 51, paddingBottom: 32,
            maxHeight: "90vh", overflowY: "auto",
            animation: "slideUp 0.32s cubic-bezier(0.32,0.72,0,1)",
          }}
          data-testid="receipt-summary-sheet"
        >
          {content}
        </div>
        {lightbox}
        <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>
      </>
    );
  }

  // ── Desktop: centred modal ─────────────────────────────────────────────────
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.35)", zIndex: 50 }}
        data-testid="sheet-overlay"
      />
      <div
        style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 480, maxWidth: "90vw",
          backgroundColor: "#FFFFFF",
          borderRadius: 20, zIndex: 51,
          paddingBottom: 24,
          animation: "scaleIn 0.22s cubic-bezier(0.32,0.72,0,1)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        }}
        data-testid="receipt-summary-sheet"
      >
        {content}
      </div>
      {lightbox}
      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: translate(-50%,-50%) scale(0.95) }
          to   { opacity: 1; transform: translate(-50%,-50%) scale(1) }
        }
      `}</style>
    </>
  );
}